import React from 'react';

import { App, Button, Dropdown, Table, Tag, Tooltip, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';

import Truncate from 'v2/components/Shared/TruncateText';
import { dateFormat } from 'v2/formatters/date';
import {
  ContractEvents,
  InvoiceEvents,
  InvoiceStatus,
  SubscriptionStatus,
  WebhookStatus,
  WebhookType
} from 'v2/interfaces/webhook2';
import type { WebhookHistory } from 'v2/interfaces/webhook2';
import webhookService from 'v2/services/webhook2';

import { Icon } from '@eduzz/ui-antd-theme';

import { useWebhookHistoryContext } from './context';
import { HistoryStatus } from './FilterWebhookHistory';
import { QuickView } from './Quickview';
import { ALL_PRODUCTS_ID } from '../WebhookForm/Fields';

const ROW_STATUS_COLOR: Record<string, string> = {
  [HistoryStatus.FAILED]: 'bg-(--uizz-antd-color-error-bg)',
  [HistoryStatus.SUCCESS]: 'bg-(--uizz-antd-color-success-bg)',
  [HistoryStatus.ARCHIVED]: '',
  [HistoryStatus.PENDING]: 'bg-(--uizz-antd-color-warning-bg)'
};

const HTTP_STATUS_COLOR: Record<string, 'success' | 'error' | 'default'> = {
  success: 'success',
  failed: 'error'
};

const messageStatus = {
  SUCCESS: 'Enviado',
  PENDING: 'Pendente',
  ARCHIVED: 'Arquivado',
  ERROR: 'Retornou erro'
} as Record<WebhookHistory['status'], string>;

export const ListWebhookHistory = () => {
  const { message } = App.useApp();
  const [submitingResend, setSubmitingResend] = React.useState<boolean>(false);
  const [openQuickview, setOpenQuickview] = React.useState<boolean>(false);
  const [currentWebhookHistory, setCurrentWebhookHistory] = React.useState<WebhookHistory>();

  const { updateList, listWebhookHistory, webhook, loading, type, pagination, setPagination } =
    useWebhookHistoryContext();

  const handleResendWebhook = async (item: WebhookHistory) => {
    setSubmitingResend(true);

    try {
      await webhookService.resendUrl(item._id);
      message.success('Webhook reenviado com sucesso');
      setSubmitingResend(false);
      updateList();
    } catch {
      message.error('Ocorreu um erro ao reenviar webhook');
      setSubmitingResend(false);
    }
  };

  const handleOpenQuickview = (item: WebhookHistory) => {
    setCurrentWebhookHistory(item);
    setOpenQuickview(true);
  };

  const handleCloseQuickview = () => setOpenQuickview(!openQuickview);

  const getEventType = (itemType: string, event?: string) => {
    if (!event) {
      return itemType;
    }

    if (Object.keys(InvoiceEvents).includes(event)) {
      return WebhookType.invoice;
    }

    if (Object.keys(ContractEvents).includes(event)) {
      return WebhookType.contract;
    }

    return WebhookType.abandonment;
  };

  const columns: ColumnsType<WebhookHistory> = [
    {
      title: 'Status',
      width: 160,
      key: 'status',
      render: (_, item) => {
        const messageState = [
          HistoryStatus.SUCCESS.toString(),
          HistoryStatus.PENDING.toString(),
          HistoryStatus.ARCHIVED.toString()
        ].includes(item.status)
          ? item.status.toUpperCase()
          : 'ERROR';
        const isTheCurrentRetryingEvent =
          webhook?.state === WebhookStatus.failing && item?.payloadId === webhook?.currentRetryingEvent;

        return (
          <div className='flex items-center gap-1'>
            <Typography.Text>{messageStatus[messageState]}</Typography.Text>
            {isTheCurrentRetryingEvent && (
              <Tooltip title='Esse é o evento que está em retentativa atualmente, caso ele seja recebido com sucesso pela sua url, ela será reativada e os eventos pendentes e com erro serão reenviados.'>
                <span className='inline-flex'>
                  <Icon.CircleHelp size={16} className='text-(--uizz-antd-color-text-secondary)' />
                </span>
              </Tooltip>
            )}
          </div>
        );
      }
    },
    ...(type !== 'abandonment'
      ? [
          {
            title: 'Fatura/Contrato',
            key: 'entityInfo',
            render: (_: unknown, item: WebhookHistory) => {
              const eventType = getEventType(item.type, item.event);

              if (eventType === 'invoice') {
                return (
                  <>
                    <div>{item.entityId}</div>
                    <div>
                      <span>{InvoiceStatus[item.entityStatus as keyof typeof InvoiceStatus] ?? '-'}</span>
                    </div>
                  </>
                );
              }

              if (eventType === 'contract') {
                return (
                  <>
                    <div>{item.entityId}</div>
                    <div>
                      <span>{SubscriptionStatus[item.entityStatus as keyof typeof SubscriptionStatus]}</span>
                    </div>
                  </>
                );
              }

              return null;
            }
          }
        ]
      : []),
    {
      title: 'Produto',
      key: 'product',
      render: (_, item) => {
        const hasProduct = item.product.id !== ALL_PRODUCTS_ID;
        if (!hasProduct) return '-';
        return (
          <>
            <Truncate count={30} final='...' title={item.product.title}>
              {item.product.title}
            </Truncate>
            {item.product.id}
          </>
        );
      }
    },
    {
      title: 'Comprador',
      key: 'customer',
      render: (_, item) => (
        <>
          <Truncate count={25} final='...' title={item.customer.name}>
            {item.customer.name}
          </Truncate>
          <Truncate count={35} final='...' title={item.customer.email}>
            {item.customer.email}
          </Truncate>
        </>
      )
    },
    {
      title: 'Data de envio',
      width: 140,
      key: 'sentAt',
      render: (_, item) => {
        const isPending = item.status === HistoryStatus.PENDING;
        if (isPending || !item.sent_at) return '-';
        return (
          <>
            {dateFormat(item.sent_at, 'dd/MM/yyyy')} <br />
            {dateFormat(item.sent_at, ' HH:mm:ss')}
          </>
        );
      }
    },
    {
      title: 'Retorno HTTP',
      width: 120,
      align: 'center',
      key: 'httpStatus',
      render: (_, item) => {
        const httpStatus = item.lastSend?.returnStatus;
        if (!httpStatus) return '-';
        return <Tag color={HTTP_STATUS_COLOR[item.status] || 'default'}>{httpStatus}</Tag>;
      }
    },
    {
      title: '',
      width: 60,
      key: 'actions',
      fixed: 'right',
      render: (_, item) => {
        const disabledResend = item.status === HistoryStatus.PENDING;
        return (
          <Dropdown
            menu={{
              items: [
                {
                  key: 'resend',
                  label: 'Reenviar webhook',
                  icon: <Icon.RefreshCw size={16} />,
                  disabled: disabledResend || submitingResend
                },
                {
                  key: 'view',
                  label: 'Visualizar detalhes',
                  icon: <Icon.Eye size={16} />
                }
              ],
              onClick: ({ key }) => {
                if (key === 'resend') handleResendWebhook(item);
                if (key === 'view') handleOpenQuickview(item);
              }
            }}
            trigger={['click']}
          >
            <Button type='text' shape='circle' aria-label='Ações do histórico' icon={<Icon.Ellipsis size={16} />} />
          </Dropdown>
        );
      }
    }
  ];

  return (
    <>
      <Table<WebhookHistory>
        columns={columns}
        dataSource={listWebhookHistory}
        scroll={{ x: 800 }}
        rowKey='_id'
        loading={loading}
        rowClassName={item => {
          const classNameStatus = ROW_STATUS_COLOR[item.status] || '';
          const isTheCurrentRetryingEvent =
            webhook?.state === WebhookStatus.failing && item?.payloadId === webhook?.currentRetryingEvent;
          return `${classNameStatus} ${isTheCurrentRetryingEvent ? 'bg-(--uizz-antd-color-error-bg)' : ''}`;
        }}
        pagination={{
          pageSizeOptions: [10, 20, 40],
          showSizeChanger: true,
          total: pagination?.total_items || 0,
          pageSize: pagination?.per_page || 10,
          current: pagination?.current_page || 1,
          locale: { items_per_page: 'Itens por página' },
          className: 'justify-end',
          onChange: (page, pageSize) => {
            if (pageSize !== (pagination?.per_page || 10)) {
              setPagination({ ...pagination, per_page: pageSize, current_page: 1 });
            } else {
              setPagination({ ...pagination, current_page: page });
            }
          }
        }}
      />

      <QuickView
        open={openQuickview}
        url={webhook}
        webhook={currentWebhookHistory}
        handleClose={handleCloseQuickview}
      />
    </>
  );
};
